// src/pages/personal/analytics/FeedbackSection/components/CategoryDistribution/CategoryChart.tsx
import React, { useMemo } from 'react';
import { BarChart, BarDataSeries } from '@/components/ui/Charts';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/hooks/useResponsive';
import { formatNumber, formatPercent } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';

interface ChartData {
  name: string;
  value: number;
  percentage: number;
  color?: string;
}

interface CategoryChartProps {
  data: ChartData[];
  period?: string; // Добавляем параметр периода
}

// Маппинг оценок на цвета (обновлено для 3-балльной шкалы)
const RATING_COLORS: Record<string, string> = {
  'Плохо': '#F44336',               // Красный - для оценки 1
  'Есть над чем работать': '#FF9800',     // Оранжевый - для оценки 2
  'Хорошо': '#4CAF50'               // Зеленый - для оценки 3
};

export const CategoryChart: React.FC<CategoryChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation('analytics');
  const { isMobile } = useResponsive();
  const locale = i18n.language || LOCALES.DEFAULT;
  
  // Адаптируем данные с добавлением цветов, если они не заданы
  const enrichedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      // Используем заданный цвет или берем из маппинга по имени, или используем дефолтный
      color: item.color || RATING_COLORS[item.name] || '#245D33'
    }));
  }, [data]);
  
  // Адаптируем данные для компонента BarChart
  const barChartData = enrichedData.map(item => ({
    name: item.name,
    responses: item.value,
    percentage: item.percentage // Добавляем для использования в подсказке
  }));

  // Определяем серии для графика
  const series: BarDataSeries[] = [
    {
      name: t('feedback.distribution.responses'),
      dataKey: 'responses',
      color: '#245D33', // Базовый цвет серии
      itemColors: enrichedData.map(item => item.color) // Цвета из обогащенных данных
    }
  ];
  
  // Функция для форматирования тултипа
  const customTooltipFormatter = (value: any, name: string, props: any) => {
    if (name === 'responses') {
      const percentage = props.payload.percentage;
      return (
        <>
          <span>{formatNumber(value, 0, true, locale)}</span>
          <span className="ml-1">({formatPercent(percentage, 1, true, locale)})</span>
        </>
      );
    }
    return value;
  };
  
  return (
    <BarChart
      data={barChartData}
      series={series}
      xAxisKey="name"
      xAxisLabel={isMobile ? undefined : t('feedback.distribution.rating_label')}
      yAxisLabel={isMobile ? undefined : t('feedback.distribution.count_label')}
      showGrid={true}
      showLegend={false}
      height={300}
      barSize={isMobile ? 30 : 40}
      borderRadius={4}
      colorsByItem={true} // Включаем режим индивидуальных цветов
      tooltipFormatter={customTooltipFormatter}
    />
  );
};